import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { map, retry, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AiPlatform } from './ai-prices.service';

/** one change the daily feed read brought: a price that moved, a model new on a platform, one gone, one retiring */
export interface AiPriceEvent {
  id: number;
  modelId: string;
  name: string;
  vendor: string;
  platform: string;
  kind: 'listed' | 'changed' | 'delisted' | 'retiring';
  field: 'input' | 'output' | 'cacheRead' | 'cacheWrite' | null;
  before: number | null;
  after: number | null;
  note: string | null;
  seenAt: string;
  day: string;
  /** the change in words, from the server */
  text: string;
}

export interface AiChanges {
  ok: boolean;
  generatedAt: string;
  platforms: { [ key: string ]: AiPlatform };
  thisMonth: { [ kind: string ]: number };
  subscribers: number;
  events: AiPriceEvent[];
}

/** the market as one row a day: the mean of each vendor's priciest (frontier) and cheapest (budget) model */
export interface AiIndexRow {
  day: string;
  frontierInput: number | null;
  frontierOutput: number | null;
  budgetInput: number | null;
  budgetOutput: number | null;
  models: number;
  vendors: number;
}

export interface AiIndex {
  ok: boolean;
  days: AiIndexRow[];
}

/**
 * AI Price Watch (owner 2026-10-28): the feed's history from `GET /ai/changes` and the daily index from
 * `GET /ai/index`, one request each per visit; `POST /ai/alerts` asks for mails. While the server cannot be
 * reached the reads are repeated every 30 seconds.
 */
@Injectable( { providedIn: 'root' } )
export class AiWatchService {

  readonly changes$: Observable<AiChanges> = this.http.get<AiChanges>( `${ environment.serverUrl }/ai/changes?limit=2000` ).pipe(
    map( data => {
      if ( !data || !data.ok || !Array.isArray( data.events ) ) { throw new Error( 'no changes' ); }
      return data;
    } ),
    retry( { delay: () => timer( 30 * 1000 ) } ),
    shareReplay( { bufferSize: 1, refCount: false } ),
  );

  readonly index$: Observable<AiIndex> = this.http.get<AiIndex>( `${ environment.serverUrl }/ai/index?days=400` ).pipe(
    map( data => {
      if ( !data || !data.ok || !Array.isArray( data.days ) ) { throw new Error( 'no index' ); }
      return data;
    } ),
    retry( { delay: () => timer( 30 * 1000 ) } ),
    shareReplay( { bufferSize: 1, refCount: false } ),
  );

  constructor( private http: HttpClient ) {
  }

  /** asks for mails; the server answers ok whatever the address, the confirmation mail decides */
  subscribe( email: string, scope: 'all' | 'vendor' | 'model', target: string | null ): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>( `${ environment.serverUrl }/ai/alerts`, { email, scope, target } );
  }

  /** the RSS feed's address */
  static feedUrl(): string {
    return `${ environment.serverUrl }/ai/changes/feed.xml`;
  }
}
